// DesignPreview.jsx — Static visual mockup of the new "Swiss High-Contrast Luxury"
// design language across all 5 customer-facing surfaces. NOT wired to live data.
// Lives at /design-preview so the user can judge the look before we rebuild
// the real PayInvoice / Book / Track / Lookup / Customer pages.

import React from "react";
import { CreditCard, MapPin, Smartphone, Check, ArrowRight, Star, ShieldCheck, Phone, Clock, Users, Briefcase } from "lucide-react";

const ACCENT = "#004EEB";

const Btn = ({ children, primary, className = "" }) => (
  <button className={`inline-flex items-center justify-center gap-2 px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-semibold transition-all duration-300 ${
    primary
      ? "bg-[#050505] text-white hover:bg-[#004EEB] hover:-translate-y-0.5 shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
      : "bg-white text-[#050505] border border-black/10 hover:border-[#050505] hover:-translate-y-0.5"
  } ${className}`}>{children}</button>
);

const Label = ({ children, className = "" }) => (
  <div className={`text-[10px] uppercase tracking-[0.22em] font-bold text-[#666] ${className}`}>{children}</div>
);

const H2 = ({ children, className = "" }) => (
  <h2 className={`font-[Outfit] text-3xl sm:text-4xl tracking-tight font-medium text-[#050505] ${className}`}>{children}</h2>
);

export default function DesignPreview() {
  return (
    <div className="min-h-screen bg-white text-[#050505]" style={{ fontFamily: "'Manrope', sans-serif" }}>

      {/* === NAV === */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="font-[Outfit] tracking-[0.3em] text-sm font-semibold">DELUX LIMO</div>
          <div className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.18em]">
            <a className="opacity-70 hover:opacity-100 transition" href="#">Home</a>
            <a className="opacity-70 hover:opacity-100 transition" href="#">Book a Ride</a>
            <a className="opacity-70 hover:opacity-100 transition" href="#">Find Reservation</a>
            <a className="opacity-70 hover:opacity-100 transition" href="#">Fleet</a>
          </div>
          <div className="flex items-center gap-3">
            <a href="tel:+14143336275" className="hidden md:inline-flex items-center gap-1.5 text-xs font-mono opacity-70 hover:opacity-100">
              <Phone size={12}/> (414) 333-6275
            </a>
            <Btn primary>Book now</Btn>
          </div>
        </div>
      </nav>

      {/* === PREVIEW HEADER === */}
      <header className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-10">
        <Label>Design preview · Swiss high-contrast luxury</Label>
        <h1 className="font-[Outfit] text-5xl sm:text-6xl tracking-tighter font-light mt-3 mb-4">
          Customer experience, <span className="italic font-normal">refined.</span>
        </h1>
        <p className="text-base text-[#666] max-w-2xl">Below: how the redesigned <b className="text-[#050505]">Pay Invoice</b>, <b className="text-[#050505]">Track</b>, <b className="text-[#050505]">Lookup</b>, <b className="text-[#050505]">Book</b> and <b className="text-[#050505]">Customer Dashboard</b> would look. Same data, same flows — new visual language.</p>
      </header>

      {/* === 1. PAY INVOICE === */}
      <Section title="1 · Pay Invoice" sub="The page customers land on from the QR code / SMS link">
        <div className="grid md:grid-cols-2 gap-6 border border-black/8 bg-[#F7F7F8]/40 p-8">
          {/* LEFT — invoice details */}
          <div className="bg-white p-7 border border-black/5">
            <Label>Invoice</Label>
            <div className="font-mono text-lg mt-1 mb-5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>INV-2606-E0A10</div>
            <div className="space-y-3 text-sm">
              <Row k="Reservation #" v={<span className="font-mono">DLX-35928F</span>}/>
              <Row k="Booked on" v="Thu, Jun 25, 2026 · 3:15 PM CDT"/>
              <Row k="Customer" v="Peter Walz"/>
              <Row k="Vehicle" v="Luxury Sedan"/>
            </div>
            <div className="my-6 border-t border-black/8"/>
            <Label>Line items</Label>
            <div className="mt-3 space-y-2.5 text-sm">
              <div className="flex justify-between"><span>Concert pickup · Hartland → Milwaukee</span><span className="font-mono">$150.00</span></div>
              <div className="flex justify-between"><span>Wait time · 45 min</span><span className="font-mono">$30.00</span></div>
              <div className="flex justify-between text-[#666]"><span>Gratuity (20%)</span><span className="font-mono">$36.00</span></div>
            </div>
            <div className="mt-6 pt-5 border-t border-black/8 flex justify-between items-baseline">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#666] font-bold">Total due</span>
              <span className="font-[Outfit] text-4xl tracking-tighter font-medium">$216.00</span>
            </div>
          </div>

          {/* RIGHT — payment */}
          <div className="space-y-4">
            {/* Saved card chip */}
            <button className="w-full border-2 border-[#004EEB] bg-[#E5F0FF] hover:bg-[#004EEB] hover:text-white group transition-all duration-300 p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard size={20} className="text-[#004EEB] group-hover:text-white"/>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold">⚡ One-tap pay · Saved on this device</div>
                  <div className="text-base font-medium mt-0.5">Visa ···· 4242 — Pay $216.00</div>
                </div>
              </div>
              <ArrowRight size={18} className="text-[#004EEB] group-hover:text-white"/>
            </button>

            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-black/10"/>
              <span className="text-[9px] uppercase tracking-[0.22em] text-[#666]">or choose another method</span>
              <div className="flex-1 h-px bg-black/10"/>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <MethodChip icon={<Smartphone size={14}/>} label="Apple Pay"/>
              <MethodChip icon={<Smartphone size={14}/>} label="Google Pay"/>
              <MethodChip icon={<CreditCard size={14}/>} label="Card"/>
              <MethodChip label="Zelle"/>
            </div>

            <div className="text-[11px] text-[#666] flex items-center gap-2 pt-2">
              <ShieldCheck size={12} className="text-[#004EEB]"/>
              Encrypted by Square · Apple Pay / Google Pay tokens never touch our server
            </div>
          </div>
        </div>
      </Section>

      {/* === 2. TRACK === */}
      <Section title="2 · Track (live driver page)" sub="What a passenger sees on their phone while the chauffeur is en route">
        <div className="relative h-[420px] border border-black/8 overflow-hidden bg-[url('https://images.unsplash.com/photo-1524661135-423995f22d0b?w=1200')] bg-cover bg-center">
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"/>
          {/* Status pipeline */}
          <div className="absolute top-6 left-6 right-6 bg-white/95 backdrop-blur-xl border border-black/5 p-5">
            <Label>Trip status</Label>
            <div className="mt-3 flex items-center gap-2">
              {["Assigned", "En route", "On location", "In car", "Dropped off"].map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`flex items-center gap-2 ${i <= 1 ? "text-[#050505]" : "text-[#999]"}`}>
                    <div className={`w-2 h-2 rounded-full ${i < 1 ? "bg-[#10B981]" : i === 1 ? "bg-[#004EEB] ring-4 ring-[#004EEB]/20" : "bg-black/10"}`}/>
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold">{s}</span>
                  </div>
                  {i < 4 && <div className={`flex-1 h-px ${i < 1 ? "bg-[#10B981]" : "bg-black/10"}`}/>}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Bottom sheet */}
          <div className="absolute left-6 right-6 bottom-6 bg-white/95 backdrop-blur-xl border border-black/5 p-6">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#050505] text-white flex items-center justify-center font-[Outfit] text-xl">MC</div>
                <div>
                  <Label>Your chauffeur</Label>
                  <div className="font-[Outfit] text-xl mt-0.5 font-medium">Marcus Chen</div>
                  <div className="text-xs text-[#666] mt-1">Lincoln Navigator · Black · WI · ABC-1234</div>
                </div>
              </div>
              <div className="text-right">
                <Label>ETA</Label>
                <div className="font-[Outfit] text-4xl tracking-tighter font-light mt-0.5">7 <span className="text-base text-[#666] font-normal tracking-normal">min</span></div>
                <div className="text-[11px] font-mono text-[#666] mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Pickup · 3:00 PM CDT</div>
              </div>
              <div className="flex gap-2">
                <Btn>Call</Btn>
                <Btn primary>Message</Btn>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* === 3. LOOKUP === */}
      <Section title="3 · Find Reservation" sub="Public lookup — guest enters email + confirmation code">
        <div className="max-w-md mx-auto bg-white border border-black/8 p-10">
          <Label>Find your reservation</Label>
          <h3 className="font-[Outfit] text-3xl tracking-tight font-light mt-2 mb-6">Quick lookup.</h3>
          <div className="space-y-4">
            <Field label="Email address" placeholder="peter@example.com"/>
            <Field label="Confirmation code" placeholder="DLX-XXXXXX" mono/>
            <Btn primary className="w-full !py-4">Find my ride <ArrowRight size={14}/></Btn>
          </div>
        </div>
      </Section>

      {/* === 4. BOOK === */}
      <Section title="4 · Book a Ride" sub="Split-screen — form on left, live route + fare on right">
        <div className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3 bg-white border border-black/8 p-8 space-y-5">
            <Label>Step 1 of 4 · Where to?</Label>
            <Field label="Pickup address" placeholder="417 Park Ave, Pewaukee, WI"/>
            <Field label="Drop-off address" placeholder="General Mitchell Airport (MKE)"/>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Date" placeholder="Jun 27, 2026"/>
              <Field label="Time" placeholder="3:00 PM"/>
              <Field label="Pax" placeholder="2"/>
            </div>
            <Btn primary className="!py-4">Continue <ArrowRight size={14}/></Btn>
          </div>
          <div className="md:col-span-2 bg-[#F7F7F8] border border-black/8 p-7">
            <Label>Live estimate</Label>
            <div className="font-[Outfit] text-5xl tracking-tighter font-light mt-2">$185 <span className="text-base text-[#666] font-normal tracking-normal">— $215</span></div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#666]">Distance</span><span className="font-mono">9 mi · 14 min</span></div>
              <div className="flex justify-between"><span className="text-[#666]">Vehicle</span><span>Luxury SUV</span></div>
              <div className="flex justify-between"><span className="text-[#666]">Gratuity (20%)</span><span className="font-mono">$31</span></div>
            </div>
            <div className="mt-5 h-48 rounded bg-[url('https://maps.googleapis.com/maps/api/staticmap?center=Milwaukee&zoom=11&size=600x300&style=feature:all|saturation:-100')] bg-cover border border-black/5"/>
          </div>
        </div>
      </Section>

      {/* === 5. CUSTOMER DASHBOARD === */}
      <Section title="5 · Customer Dashboard" sub="Logged-in repeat customers — bento grid">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-[#050505] text-white p-8 border border-black/5 relative overflow-hidden">
            <Label className="!text-white/60">Next ride</Label>
            <div className="font-[Outfit] text-4xl tracking-tight font-light mt-2 mb-1">Friday · Mar 8</div>
            <div className="text-[#999]">3:00 PM CDT · MKE Airport</div>
            <Btn className="!bg-white !text-black mt-6">Track ride</Btn>
            <div className="absolute right-6 top-6 text-7xl text-white/5 font-[Outfit]">02</div>
          </div>
          <div className="bg-white border border-black/8 p-6">
            <Label>Saved cards</Label>
            <div className="mt-3 space-y-2.5">
              <div className="flex items-center justify-between text-sm"><span>Visa ···· 4242</span><Check size={14} className="text-[#10B981]"/></div>
              <div className="flex items-center justify-between text-sm text-[#666]"><span>+ Add new card</span></div>
            </div>
          </div>
          <div className="bg-white border border-black/8 p-6">
            <Label>Referral</Label>
            <div className="font-mono text-2xl mt-2" style={{ fontFamily: "'JetBrains Mono', monospace" }}>WALZ20</div>
            <div className="text-xs text-[#666] mt-1">$20 off for each friend&apos;s first ride</div>
          </div>
          <div className="md:col-span-2 bg-white border border-black/8 p-6">
            <Label>Past trips · 24</Label>
            <div className="mt-3 space-y-2.5 text-sm">
              <TripRow date="Feb 14" route="Pewaukee → Summerfest" status="Completed" fare="$139"/>
              <TripRow date="Feb 8" route="Hilton Milwaukee → MKE" status="Completed" fare="$95"/>
              <TripRow date="Jan 30" route="Home → ORD" status="Completed" fare="$285"/>
            </div>
          </div>
        </div>
      </Section>

      {/* === TRUST SIGNALS === */}
      <Section title="Trust signals (everywhere)" sub="Like Black Car Everywhere but rendered in editorial black-and-white" tight>
        <div className="flex items-center gap-10 flex-wrap py-6 border-y border-black/8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#050505] text-white flex items-center justify-center text-xs font-bold">BBB</div>
            <div>
              <Label>A+ Rated</Label>
              <div className="font-mono text-xs mt-0.5">Accredited since 2018</div>
            </div>
          </div>
          {[1,2,3,4,5].map(i => <Star key={i} size={16} fill="#050505" className="text-[#050505]"/>)}
          <div className="font-[Outfit] text-sm font-medium">EXCELLENT — based on 1,200+ reviews</div>
          <div className="flex items-center gap-4 ml-auto text-xs text-[#666] uppercase tracking-[0.18em]">
            <span>Google 5.0</span>
            <span>Yelp 4.9</span>
            <span>TripAdvisor 5.0</span>
          </div>
        </div>
      </Section>

      <footer className="bg-[#050505] text-white py-16 mt-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <div className="font-[Outfit] tracking-[0.3em] text-sm font-semibold mb-3">DELUX LIMO TRANS GROUP</div>
          <div className="text-white/50 text-xs">Milwaukee · Available 24/7 · (414) 333-6275</div>
        </div>
      </footer>
    </div>
  );
}

const Section = ({ title, sub, children, tight }) => (
  <section className={`max-w-[1400px] mx-auto px-6 md:px-12 ${tight ? "pb-8" : "pb-20"}`}>
    <div className="mb-6 pt-4 border-t border-black/8">
      <Label>{title}</Label>
      {sub && <div className="text-sm text-[#666] mt-1">{sub}</div>}
    </div>
    {children}
  </section>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-[#666] text-xs uppercase tracking-[0.18em]">{k}</span>
    <span className="text-[#050505] text-sm text-right">{v}</span>
  </div>
);

const MethodChip = ({ icon, label }) => (
  <button className="border border-black/15 hover:border-[#050505] py-4 px-3 flex items-center justify-center gap-2 text-sm uppercase tracking-[0.16em] font-medium transition">
    {icon}{label}
  </button>
);

const Field = ({ label, placeholder, mono }) => (
  <label className="block">
    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#666] mb-1.5">{label}</div>
    <input placeholder={placeholder} className={`w-full border border-black/15 px-4 py-3 text-sm focus:border-[#004EEB] focus:ring-2 focus:ring-[#004EEB]/10 focus:outline-none transition ${mono ? "font-mono" : ""}`}/>
  </label>
);

const TripRow = ({ date, route, status, fare }) => (
  <div className="flex items-center justify-between py-2 border-b border-black/5 last:border-0">
    <div className="flex items-center gap-3">
      <span className="font-mono text-xs text-[#666] w-16" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{date}</span>
      <span>{route}</span>
    </div>
    <div className="flex items-center gap-4">
      <span className="text-[10px] uppercase tracking-[0.18em] text-[#10B981]">{status}</span>
      <span className="font-mono w-16 text-right" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fare}</span>
    </div>
  </div>
);
